import { Link } from "@tanstack/react-router";
// Import React.lazy and Suspense
import React, { lazy, Suspense } from "react";
import { servicesListData } from "@/lib/data/services-data";
import ImageKit from "@/lib/utils/imageKit";
import ChevronButton from "../../ui/chevron-button";

// Dynamically import DesktopNav and MobileNav
const LazyDesktopNav = lazy(() =>
	import("./desktop-nav").then((m) => ({ default: m.DesktopNav })),
);
const LazyMobileNav = lazy(() =>
	import("./mobile-nav").then((m) => ({ default: m.MobileNav })),
);

const DEFAULT_NAV_LINKS = [
	{ id: 1, href: "/", label: "Home" },
	{ id: 2, href: "/services/", label: "Services", items: servicesListData },
	{ id: 3, href: "/portfolio/", label: "Portfolio" },
	{ id: 4, href: "/blog/", label: "Blog" },
	{ id: 5, href: "/about/", label: "About" },
];

function Header({
	navLinks = DEFAULT_NAV_LINKS,
}: {
	navLinks?: typeof DEFAULT_NAV_LINKS;
}) {
	return (
		<header className="sticky top-0 z-50 w-full backdrop-blur supports-backdrop-filter:bg-background/80 border-b border-border/40">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<nav
					className="flex h-16 items-center justify-between"
					aria-label="Global"
				>
					{/* Logo */}
					<Link
						to="/"
						className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange rounded-md p-1"
						aria-label="Craftlayer Home"
					>
						<ImageKit
							path="logo.svg"
							alt="Craftlayer Logo"
							width={120}
							height={40}
							className="h-auto w-30"
							priority
							fetchPriority="high"
						/>
					</Link>

					{/* Desktop navigation */}
					<Suspense
						fallback={
							<div className="h-4 w-64 animate-pulse bg-muted rounded-full" />
						}
					>
						<LazyDesktopNav navLinks={navLinks} />
					</Suspense>

					{/* CTA + Mobile Menu */}
					<div className="flex items-center gap-4">
						<Link to="/contact/" aria-label="Book a call">
							<ChevronButton
								text="Book a call"
								className="rounded-full hidden md:flex"
								as="div"
							/>
						</Link>
						{/* Mobile Menu */}
						<Suspense
							fallback={
								<div className="h-8 w-8 animate-pulse bg-muted rounded-md" />
							}
						>
							<LazyMobileNav navLinks={navLinks} />
						</Suspense>
					</div>
				</nav>
			</div>
		</header>
	);
}

export default React.memo(Header);
